import { z } from "@reflag/shared/utils/zod";

import { APIResponse } from "./api";
import {
  EnvironmentSelectionQuerySchema,
  EnvironmentSelectionQueryType,
} from "./environmentAPI";
import { Entity } from "./featureAPI";
import {
  COMPANY_ID_CONTEXT_FIELD,
  FlagFilterSchema,
  getFilterCount,
  MAX_ROLLOUT_THRESHOLD,
} from "./filter";
import { NotificationProviderSchema } from "./notification";
import { booleanish } from "./schemas/booleanish";
import { PaginationQueryBaseSchema } from "./schemas/dataTableSchema";
import { ExternalIdArraySchema } from "./schemas/external";
import { Paginated } from "./types/Paginated";
import { ReflagUser } from "./types/ReflagUser";
import { SummaryItem } from "./utils/summaryItem";

const partialRolloutThresholdErrorMessage =
  "Rollout threshold should be between 0% and 100%";

export const CustomRuleSchema = z
  .object({
    filter: FlagFilterSchema,
    partialRolloutThreshold: z
      .number(partialRolloutThresholdErrorMessage)
      .int()
      .min(0)
      .max(MAX_ROLLOUT_THRESHOLD, {
        message: partialRolloutThresholdErrorMessage,
      })
      .default(MAX_ROLLOUT_THRESHOLD),
    partialRolloutContextAttribute: z
      .string()
      .default(COMPANY_ID_CONTEXT_FIELD),
  })
  .strict()
  .refine(
    (rule) => {
      return (
        rule.partialRolloutThreshold === MAX_ROLLOUT_THRESHOLD ||
        getFilterCount(rule.filter) > 0
      );
    },
    {
      message: "You must provide a filter for gradual roll-out.",
      path: ["filter"],
    },
  );

export type CustomRule = z.input<typeof CustomRuleSchema>;

export const FeatureTargetingModes = ["none", "some", "everyone"] as const;
export type FeatureTargetingMode = (typeof FeatureTargetingModes)[number];
export const FeatureTargetingSchema = z
  .object({
    targetingMode: z.enum(FeatureTargetingModes),
    segmentIds: z.array(z.string()),
    companyIds: ExternalIdArraySchema,
    userIds: ExternalIdArraySchema,
    customRules: z.array(CustomRuleSchema),
  })
  .strict();

export type FeatureTargeting = z.input<typeof FeatureTargetingSchema>;
export type FeatureTargetingOutput = z.output<typeof FeatureTargetingSchema>;

export function transformFeatureTargeting<T extends FeatureTargeting>(
  version: T,
): T {
  return {
    ...version,
    customRules: version.customRules.filter((rule) => {
      return getFilterCount(rule.filter) > 0;
    }),
  };
}

export const UpdateEnvironmentFeatureTargetingSchema =
  FeatureTargetingSchema.extend({
    environmentId: z.string(),
  })
    .strict()
    .transform(transformFeatureTargeting);

export type UpdateEnvironmentFeatureTargeting = z.input<
  typeof UpdateEnvironmentFeatureTargetingSchema
>;

export const UpdateEnvironmentsFeatureTargetingSchema = z
  .array(UpdateEnvironmentFeatureTargetingSchema)
  .refine(
    (versions) => {
      const environmentIds = versions.flatMap(({ environmentId }) =>
        environmentId ? [environmentId] : [],
      );
      return new Set(environmentIds).size === environmentIds.length;
    },
    { message: "Duplicate environmentIds are not allowed" },
  );

export const UpdateFeatureTargetingBodySchema = z.object({
  stageId: z.string().nullish(),
  changeDescription: z.string().max(512).nullish(),
  notifications: NotificationProviderSchema.array().optional(),
  versions: UpdateEnvironmentsFeatureTargetingSchema,
});

export type UpdateFeatureTargetingBody = z.input<
  typeof UpdateFeatureTargetingBodySchema
>;

export const FEATURE_TARGETING_VERSION_CONFLICT_MESSAGE =
  "Attempting to update flag targeting for an old version";

export const UpdateFeatureTargetingQueryArgsSchema = z.object({
  currentVersionIds: z.string().array().optional(),
});

export type UpdateFeatureTargetingQueryArgs = z.input<
  typeof UpdateFeatureTargetingQueryArgsSchema
>;

export type FeatureTargetingVersion = FeatureTargeting & {
  id: string;
  environment: {
    id: string;
    isProduction: boolean;
    name: string;
    order: number;
  };

  isValid: boolean;

  version: number;
  currentVersion: boolean;

  createdAt: string;
  createdBy: ReflagUser | null;

  changeDescription: string | null;
  targetingSummary?: SummaryItem<Entity>[] | null;
  targetingSummaryChange?: {
    addedEntities: SummaryItem<Entity>[];
    removedEntities: SummaryItem<Entity>[];
    oldTargetingMode: FeatureTargetingMode;
    newTargetingMode: FeatureTargetingMode;
  } | null;
};

export const FeatureTargetingHistoryQueryArgsSchema = PaginationQueryBaseSchema(
  {
    pageSize: 50,
    sortOrder: "desc",
  },
)
  .extend(EnvironmentSelectionQuerySchema.shape)
  .extend({
    sortOrder: z.literal("desc").optional().default("desc"),
    includeCurrentVersion: booleanish.optional().default(true),
    includeTargetingSummary: booleanish.optional().default(false),
  });

export type FeatureTargetingHistoryQueryArgs = z.input<
  typeof FeatureTargetingHistoryQueryArgsSchema
>;

export const UpdateFeaturesTargetingBodySchema = z
  .object({
    targets: z
      .array(
        z
          .object({
            id: z.string().optional(),
            key: z.string().optional(),
            enabled: z.boolean(),
            userId: z.string().optional(),
            companyId: z.string().optional(),
            segmentId: z.string().optional(),
          })
          .strict()
          .superRefine((data, ctx) => {
            // Check that either id or key is provided, but not both
            if (!(!data.id !== !data.key)) {
              ctx.addIssue({
                code: "custom",
                message:
                  "Either the feature's id or key must be provided, but not both.",
              });
            }

            // Check that exactly one of userId, companyId, or segmentId is provided
            const ids = [data.userId, data.companyId, data.segmentId].filter(
              Boolean,
            );
            if (ids.length !== 1) {
              ctx.addIssue({
                code: "custom",
                message:
                  "Exactly one of userId, companyId, or segmentId must be provided.",
              });
            }
          }),
      )
      .min(1, {
        message: "At least one target must be provided.",
      }),
    changeDescription: z.string().optional(),
    notifications: NotificationProviderSchema.array().optional(),
  })
  .strict();

export type UpdateFeaturesTargetingBody = z.input<
  typeof UpdateFeaturesTargetingBodySchema
>;

export const FeatureTargetingContextKeysQueryArgsSchema = z
  .object({
    prefix: z.enum(["user", "company", "other"]),
    filter: z.string().optional(),
    includeTypes: booleanish.optional().default(false),
  })
  .extend(EnvironmentSelectionQuerySchema.shape)
  .strict();

export type FeatureTargetingContextKeysQueryArgs = z.input<
  typeof FeatureTargetingContextKeysQueryArgsSchema
>;

export const FeatureTargetingContextKeyValuesQueryArgsSchema = z
  .object({
    key: z.string().min(1),
    filter: z.string().optional(),
  })
  .extend(EnvironmentSelectionQuerySchema.shape)
  .strict();

export type FeatureTargetingContextKeyValuesQueryArgs = z.input<
  typeof FeatureTargetingContextKeyValuesQueryArgsSchema
>;

export const FeatureOptInMembershipsSchema = z
  .object({
    userIds: ExternalIdArraySchema,
    companyIds: ExternalIdArraySchema,
  })
  .strict();

export type FeatureOptInMemberships = z.output<
  typeof FeatureOptInMembershipsSchema
>;

export type UpdateFeatureOptInMemberships = z.input<
  typeof FeatureOptInMembershipsSchema
>;

export type FeatureOptInMembershipsUpdateResult = FeatureOptInMemberships & {
  flagStateVersion: number;
};

export interface FeatureTargetingAPI {
  "/apps/:appId/features/targeting": {
    PATCH: {
      body: UpdateFeaturesTargetingBody;
      response: APIResponse<{
        newVersions: FeatureTargetingVersion[];
      }>;
      params: { appId: string };
      query: EnvironmentSelectionQueryType;
    };
  };
  "/apps/:appId/features/:featureId/targeting": {
    GET: {
      response: APIResponse<{
        currentVersions: FeatureTargetingVersion[];
      }>;
      params: {
        appId: string;
        featureId: string;
      };
    };
    PATCH: {
      body: UpdateFeatureTargetingBody;
      response: APIResponse<{
        newVersions: FeatureTargetingVersion[];
      }>;
      params: { appId: string; featureId: string };
      query: z.input<typeof UpdateFeatureTargetingQueryArgsSchema>;
    };
  };
  "/apps/:appId/features/:featureId/opt-in-memberships": {
    GET: {
      response: APIResponse<FeatureOptInMemberships>;
      params: {
        appId: string;
        featureId: string;
      };
      query: EnvironmentSelectionQueryType;
    };
    PATCH: {
      body: UpdateFeatureOptInMemberships;
      response: APIResponse<FeatureOptInMembershipsUpdateResult>;
      params: {
        appId: string;
        featureId: string;
      };
      query: EnvironmentSelectionQueryType;
    };
  };
  "/apps/:appId/features/:featureId/targeting/history": {
    GET: {
      response: APIResponse<Paginated<FeatureTargetingVersion, "version">>;
      params: {
        appId: string;
        featureId: string;
      };
      query: z.input<typeof FeatureTargetingHistoryQueryArgsSchema>;
    };
  };
  "/apps/:appId/features/targeting/context-keys": {
    GET: {
      response: APIResponse<
        (string | { key: string; observedTypes: string[] })[]
      >;
      params: {
        envId: string;
      };
      query: FeatureTargetingContextKeysQueryArgs;
    };
  };
  "/apps/:appId/features/targeting/context-values": {
    GET: {
      response: APIResponse<string[]>;
      params: {
        envId: string;
      };
      query: FeatureTargetingContextKeyValuesQueryArgs;
    };
  };
}
